/**
 * CarrierPassiveSkillEffect — Poison tree plague path passive (Fork 1 gate).
 *
 * When an enemy dies while carrying `poison`, spawns a `carrier_cloud`
 * toxic zone at the death position. Cloud duration scales with how much
 * DoT remained on the dying enemy — more unused poison yields a longer
 * cloud. The corpse becomes a passive contamination point.
 *
 * Mechanically a thin registration layer: on construction, pushes a
 * `DeathTriggerConfig` into the `DeathTriggerSystem` with
 * `requireStatus: 'poison'` and a `spawnZone` effect. The actual zone
 * spawning happens at integration time when the caller processes the
 * `DeathTriggerEvent`. The `deathCloudScalesWithRemainingDot` boolean
 * signals the integration layer to replace the static `durationMs`
 * with the dying enemy's `poison.remainingMs`.
 *
 * Dormancy (later passive) adds a 2-second invisibility window to
 * carrier clouds — that behavior layers on top of this registration.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Carrier (plague_path_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { DeathTriggerConfig } from '../../../systems/DeathTriggerSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CarrierPassiveDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly deathTriggers: {
    register(config: DeathTriggerConfig): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CarrierPassiveConfig {
  readonly cloudRadiusTiles: number;
  /** Base duration in ms — overridden by remaining DoT when scaling is active. */
  readonly baseCloudDurationMs: number;
}

const DEFAULT_CONFIG: CarrierPassiveConfig = {
  cloudRadiusTiles: 1.5,
  baseCloudDurationMs: 4000,
};

// ---------------------------------------------------------------------------
// Trigger ID constant
// ---------------------------------------------------------------------------

const TRIGGER_ID = 'carrier_cloud_spawn';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CarrierPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'plague_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: CarrierPassiveDeps;
  private readonly config: CarrierPassiveConfig;

  private _deathTriggerRegistered = false;

  constructor(deps: CarrierPassiveDeps, config: Partial<CarrierPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Passive — register death trigger immediately on construction
    this.registerDeathTrigger();
  }

  // --- State getters ---

  get deathTriggerRegistered(): boolean { return this._deathTriggerRegistered; }

  /**
   * Whether the carrier cloud duration scales with the dying enemy's
   * remaining poison DoT. Always true when this passive is active (the
   * boolean key lives in the JSON data), but exposed as a getter so the
   * integration layer can query it directly.
   */
  get scalesWithRemainingDot(): boolean {
    return this.deps.resolver.hasEffect(
      'plague_path_passive', 'deathCloudScalesWithRemainingDot',
    );
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic
  }

  destroy(): void {
    if (this._deathTriggerRegistered) {
      this.deps.deathTriggers.unregister(TRIGGER_ID);
      this._deathTriggerRegistered = false;
    }
  }

  // --- Internal ---

  private registerDeathTrigger(): void {
    if (this._deathTriggerRegistered) return;

    this.deps.deathTriggers.register({
      id: TRIGGER_ID,
      sourceSkillId: 'plague_path_passive',
      requireStatus: 'poison',
      spawnZone: {
        zoneType: 'carrier_cloud',
        radiusTiles: this.config.cloudRadiusTiles,
        durationMs: this.config.baseCloudDurationMs,
      },
    });

    this._deathTriggerRegistered = true;
  }
}
